"use client";

import { Reorder } from "framer-motion";
import { GripVertical, ImageOff, X } from "lucide-react";
import type { Waypoint } from "./types";
import { useTheme } from "@/features/theme";

// ── Drag handle ───────────────────────────────────────────────────────────────

export function DragHandle() {
  return (
    <div className="cursor-grab active:cursor-grabbing p-1.5 text-tone-500 hover:text-tone-300 transition-colors touch-none">
      <GripVertical className="w-3.5 h-3.5" />
    </div>
  );
}

// ── Single waypoint row ───────────────────────────────────────────────────────

export function WaypointRow({
  waypoint,
  index,
  onRemove,
  isDark: isDarkProp,
}: {
  waypoint: Waypoint;
  index: number;
  onRemove: () => void;
  isDark: boolean;
}) {
  const { isDark: themeIsDark } = useTheme();
  const isDark = isDarkProp ?? themeIsDark;
  return (
    <Reorder.Item value={waypoint} id={waypoint.panoId} className="select-none">
      <div
        className={`flex items-center gap-2 p-2 rounded-xl border transition-colors ${
          isDark
            ? "border-white/5 bg-white/[0.03] hover:bg-white/[0.06]"
            : "border-neutral-200 bg-neutral-50 hover:bg-neutral-100"
        }`}
      >
        <DragHandle />
        <span
          className={`text-xs w-4 text-center font-mono shrink-0 ${
            isDark ? "text-neutral-500" : "text-neutral-400"
          }`}
        >
          {index + 1}
        </span>
        <div
          className={`w-10 h-7 rounded-lg overflow-hidden shrink-0 ${
            isDark ? "bg-neutral-800" : "bg-neutral-200"
          }`}
        >
          {waypoint.thumbnailUrl ? (
            <img src={waypoint.thumbnailUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageOff className="w-3 h-3 text-tone-600" />
            </div>
          )}
        </div>
        <p
          className={`flex-1 text-xs truncate font-medium ${
            isDark ? "text-neutral-200" : "text-neutral-800"
          }`}
        >
          {waypoint.name}
        </p>
        <button
          onClick={onRemove}
          className="p-1 rounded-lg text-tone-600 hover:text-red-400 hover:bg-red-500/10 transition-colors"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </Reorder.Item>
  );
}

// ── Reorderable list ──────────────────────────────────────────────────────────

export default function WaypointList({
  waypoints,
  onReorder,
  onRemove,
  isDark: isDarkProp,
}: {
  waypoints: Waypoint[];
  onReorder: (v: Waypoint[]) => void;
  onRemove: (panoId: string) => void;
  isDark: boolean;
}) {
  const { isDark: themeIsDark } = useTheme();
  const isDark = isDarkProp ?? themeIsDark;
  if (waypoints.length === 0) return null;

  return (
    <Reorder.Group
      axis="y"
      values={waypoints}
      onReorder={onReorder}
      className="space-y-1.5"
    >
      {waypoints.map((wp, i) => (
        <WaypointRow
          key={wp.panoId}
          waypoint={wp}
          index={i}
          onRemove={() => onRemove(wp.panoId)}
          isDark={isDark}
        />
      ))}
    </Reorder.Group>
  );
}
